import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { listDataSources, replaceDataSourceFile, DataSource, batchGenerate, listMappings, updateMapping, Mapping, getGeneratedPDFs, GeneratedPDF, downloadGeneratedPDF, deleteGeneratedPDF, deleteAllGeneratedPDFs, downloadAllGeneratedPDFs, listPDFTemplates, getPDFFields, PDFTemplate, PDFField } from '../api/client';
import './Overview.css';

const PDFGeneratePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const mappingId = searchParams.get('mapping');

  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [templateSources, setTemplateSources] = useState<DataSource[]>([]);
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Mapping | null>(null);
  const [generatedPDFs, setGeneratedPDFs] = useState<GeneratedPDF[]>([]);
  const [sortBy, setSortBy] = useState<'name' | 'date'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [pdfTemplates, setPdfTemplates] = useState<PDFTemplate[]>([]);
  const [showTemplateSwitch, setShowTemplateSwitch] = useState(false);
  const [checkingTemplate, setCheckingTemplate] = useState<string | null>(null);
  const [templateError, setTemplateError] = useState<string | null>(null);

  useEffect(() => {
    loadDataSources();
    if (mappingId) {
      loadGeneratedPDFs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappingId]);

  const loadDataSources = async () => {
    try {
      let usedSourceIds: string[] = [];
      if (mappingId) {
        const mappings = await listMappings();
        const foundMapping = mappings.find((m) => m.id === mappingId);
        if (foundMapping) {
          setMapping(foundMapping);
          usedSourceIds = foundMapping.data_sources?.map((ds) => ds.id) || [];
        }
      }

      const [allSources, allPdfTemplates] = await Promise.all([
        listDataSources(),
        listPDFTemplates(),
      ]);
      setDataSources(allSources);
      setPdfTemplates(allPdfTemplates);

      // Filter template sources that are used in this mapping
      const templates = allSources.filter((ds) =>
        ds.is_template && usedSourceIds.includes(ds.id)
      );
      setTemplateSources(templates);
    } catch (err) {
      // Error silently handled
    }
  };

  const loadGeneratedPDFs = async () => {
    if (!mappingId) return;

    try {
      const pdfs = await getGeneratedPDFs(mappingId);
      setGeneratedPDFs(pdfs || []);
    } catch (err) {
      // Error silently handled
    }
  };

  const handleFileReplace = async (dsId: string, file: File) => {
    setUploading({ ...uploading, [dsId]: true });
    setError(null);

    try {
      await replaceDataSourceFile(dsId, file);
      alert(`Datei für "${templateSources.find(ds => ds.id === dsId)?.name}" erfolgreich ersetzt!`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to replace file');
    } finally {
      setUploading({ ...uploading, [dsId]: false });
    }
  };

  const handleGenerate = async () => {
    if (!mappingId) {
      alert('Fehler: Keine Mapping-ID vorhanden');
      return;
    }

    setGenerating(true);
    setError(null);

    try {
      const result = await batchGenerate({
        mapping_id: mappingId,
        flatten: true,
      });

      alert(`Erfolgreich! ${result.count} PDF(s) generiert`);

      // Reload generated PDFs list
      await loadGeneratedPDFs();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate PDFs');
    } finally {
      setGenerating(false);
    }
  };

  const handleDeletePDF = async (pdfId: string, fileName: string) => {
    if (!window.confirm(`Möchten Sie "${fileName}" wirklich löschen?`)) {
      return;
    }

    try {
      await deleteGeneratedPDF(pdfId);
      await loadGeneratedPDFs();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete PDF');
    }
  };

  const handleDeleteAllPDFs = async () => {
    if (!mappingId) return;
    if (!window.confirm(`Möchten Sie wirklich ALLE ${generatedPDFs.length} PDFs löschen?`)) {
      return;
    }

    try {
      await deleteAllGeneratedPDFs(mappingId);
      await loadGeneratedPDFs();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete PDFs');
    }
  };

  const handleDownloadAllPDFs = async () => {
    if (!mappingId || !mapping) return;

    try {
      await downloadAllGeneratedPDFs(mappingId, mapping.name);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to download PDFs');
    }
  };

  const handleSwitchTemplate = async (newTemplateId: string) => {
    if (!mapping || !mappingId) return;
    setCheckingTemplate(newTemplateId);
    setTemplateError(null);

    try {
      // Get fields of the new PDF template
      const newPdfData = await getPDFFields(newTemplateId);
      const newFieldNames = new Set(newPdfData.fields.map((f: PDFField) => f.name));

      // Get fields used in the current mapping
      const usedPdfFields: string[] = [];
      for (const sourceId of Object.keys(mapping.field_mappings || {})) {
        for (const pdfField of Object.values(mapping.field_mappings[sourceId])) {
          if (pdfField && usedPdfFields.indexOf(pdfField) === -1) {
            usedPdfFields.push(pdfField);
          }
        }
      }

      // Check which mapped fields are missing in the new template
      const missingFields = usedPdfFields.filter(f => !newFieldNames.has(f));

      if (missingFields.length > 0) {
        setTemplateError(
          `Das PDF-Formular "${newPdfData.name}" hat nicht alle benötigten Felder. ` +
          `Fehlende Felder: ${missingFields.join(', ')}`
        );
        setCheckingTemplate(null);
        return;
      }

      // All fields match - update the mapping
      await updateMapping(mappingId, { pdf_template_id: newTemplateId });
      setMapping({ ...mapping, pdf_template_id: newTemplateId });
      setShowTemplateSwitch(false);
      setTemplateError(null);
      alert('PDF-Formular gewechselt!');
    } catch (err: any) {
      setTemplateError(err.response?.data?.error || err.message);
    } finally {
      setCheckingTemplate(null);
    }
  };

  const getCurrentTemplateName = () => {
    return pdfTemplates.find(t => t.id === mapping?.pdf_template_id)?.name || mapping?.pdf_template_id || '';
  };

  const toggleSort = (field: 'name' | 'date') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder(field === 'date' ? 'desc' : 'asc');
    }
  };

  const sortedPDFs = [...generatedPDFs].sort((a, b) => {
    let comparison = 0;
    if (sortBy === 'name') {
      comparison = a.file_name.localeCompare(b.file_name, 'de');
    } else {
      comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    }
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  if (!mappingId || !mapping) {
    return (
      <div className="overview-container">
        <h1>PDFs Generieren</h1>
        <div className="empty-state">
          <h3>Kein Projekt ausgewählt</h3>
          <p>Bitte wählen Sie ein Projekt aus der Projektübersicht aus.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="overview-container">
      <div className="overview-header">
        <div>
          <h1>PDFs Generieren</h1>
          <p className="overview-description">
            Projekt: <strong>{mapping.name}</strong>
          </p>
        </div>
        <button
          onClick={handleGenerate}
          className="btn-primary"
          disabled={generating}
          style={{ fontSize: '1.1rem', padding: '0.75rem 1.5rem' }}
        >
          {generating ? 'Generiere PDFs...' : '🚀 PDFs Generieren'}
        </button>
      </div>

      {/* PDF Template Switch */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <strong>📄 PDF-Formular:</strong>{' '}
            <span style={{ color: '#666' }}>{getCurrentTemplateName()}</span>
          </div>
          <button
            onClick={() => { setShowTemplateSwitch(!showTemplateSwitch); setTemplateError(null); }}
            className="btn-small btn-secondary"
          >
            {showTemplateSwitch ? 'Abbrechen' : 'PDF wechseln'}
          </button>
        </div>

        {showTemplateSwitch && (
          <div style={{ marginTop: '1rem', borderTop: '1px solid #e0e0e0', paddingTop: '1rem' }}>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
              Nur PDF-Formulare mit den gleichen Feldern können verwendet werden.
            </p>
            {templateError && (
              <div style={{ padding: '0.75rem', background: '#ffebee', color: '#c62828', borderRadius: '4px', marginBottom: '0.75rem', fontSize: '0.9rem' }}>
                {templateError}
              </div>
            )}
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {pdfTemplates
                .filter(t => t.id !== mapping?.pdf_template_id)
                .map(t => (
                  <div key={t.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '0.75rem', background: '#f9f9f9', borderRadius: '4px', border: '1px solid #e0e0e0'
                  }}>
                    <div>
                      <strong>{t.name}</strong>
                      <span style={{ color: '#666', fontSize: '0.85rem', marginLeft: '0.5rem' }}>
                        {t.fields.length} Felder
                      </span>
                    </div>
                    <button
                      onClick={() => handleSwitchTemplate(t.id)}
                      className="btn-small btn-primary"
                      disabled={checkingTemplate === t.id}
                    >
                      {checkingTemplate === t.id ? 'Prüfe...' : 'Auswählen'}
                    </button>
                  </div>
                ))}
              {pdfTemplates.filter(t => t.id !== mapping?.pdf_template_id).length === 0 && (
                <p style={{ color: '#666' }}>Keine weiteren PDF-Formulare vorhanden.</p>
              )}
            </div>
          </div>
        )}
      </div>

      {templateSources.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>Datendateien auswählen</h3>
          <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.95rem' }}>
            Lade die aktuellen Datendateien für jede Datenvorlage hoch.
            Die Struktur (Spalten) muss mit der ursprünglichen Vorlage übereinstimmen.
          </p>

          <div className="grid-layout">
            {templateSources.map((ds) => (
              <div key={ds.id} className="card">
                <div className="card-header">
                  <h3>{ds.name}</h3>
                  <span className={`badge badge-${ds.type}`}>{ds.type.toUpperCase()}</span>
                </div>
                <div className="card-meta">
                  <div>
                    <strong>Typ:</strong> {ds.type}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#666', wordBreak: 'break-all' }}>
                    <strong>Aktuelle Datei:</strong><br />
                    {ds.config.file_path || 'Nicht verfügbar'}
                  </div>
                </div>
                <div className="card-actions">
                  <label
                    htmlFor={`file-${ds.id}`}
                    className="btn-small btn-primary"
                    style={{
                      cursor: 'pointer',
                      display: 'inline-block',
                      background: uploading[ds.id] ? '#ccc' : '#006355',
                      color: 'white',
                      textAlign: 'center',
                      flex: 1,
                    }}
                  >
                    {uploading[ds.id] ? 'Uploading...' : '📁 Neue Datei hochladen'}
                  </label>
                  <input
                    id={`file-${ds.id}`}
                    type="file"
                    accept={ds.type === 'json' ? '.json' : ds.type === 'csv' ? '.csv' : '.xlsx,.xls'}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileReplace(ds.id, file);
                    }}
                    style={{ display: 'none' }}
                    disabled={uploading[ds.id]}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {templateSources.length === 0 && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <p style={{ margin: 0, color: '#666' }}>
            Keine Datenvorlagen vorhanden. Die PDFs werden mit den ursprünglichen Dateien generiert.
          </p>
        </div>
      )}

      <div className="card">
        <h3>Zusammenfassung</h3>
        <div className="card-meta">
          <div><strong>Projekt:</strong> {mapping.name}</div>
          <div><strong>PDF-Formular:</strong> {mapping.pdf_template_id}</div>
          <div><strong>Datenvorlagen:</strong> {mapping.data_sources?.length || 0}</div>
          <div><strong>Beziehungen:</strong> {mapping.relationships?.length || 0}</div>
        </div>
      </div>

      {error && (
        <div style={{
          padding: '1rem',
          background: '#ffebee',
          color: '#c62828',
          borderRadius: '4px',
          marginTop: '1rem'
        }}>
          {error}
        </div>
      )}

      {generatedPDFs.length > 0 && (
        <div style={{ marginTop: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <h3 style={{ margin: 0 }}>Generierte PDFs ({generatedPDFs.length})</h3>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <span style={{ fontSize: '0.85rem', color: '#666' }}>Sortieren:</span>
              <button
                onClick={() => toggleSort('name')}
                className="btn-small"
                style={{
                  background: sortBy === 'name' ? '#006355' : '#e0e0e0',
                  color: sortBy === 'name' ? 'white' : '#333'
                }}
              >
                Name {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
              </button>
              <button
                onClick={() => toggleSort('date')}
                className="btn-small"
                style={{
                  background: sortBy === 'date' ? '#006355' : '#e0e0e0',
                  color: sortBy === 'date' ? 'white' : '#333'
                }}
              >
                Datum {sortBy === 'date' && (sortOrder === 'asc' ? '↑' : '↓')}
              </button>
              <span style={{ margin: '0 0.25rem' }}>|</span>
              <button
                onClick={handleDownloadAllPDFs}
                className="btn-small"
                style={{ background: '#1976d2', color: 'white' }}
              >
                📦 Alle als ZIP
              </button>
              <button
                onClick={handleDeleteAllPDFs}
                className="btn-small btn-danger"
              >
                🗑️ Alle löschen
              </button>
            </div>
          </div>
          <div className="grid-layout">
            {sortedPDFs.map((pdf) => (
              <div key={pdf.id} className="card">
                <div className="card-header">
                  <h3 style={{ fontSize: '1rem', wordBreak: 'break-word' }}>{pdf.file_name}</h3>
                </div>
                <div className="card-meta">
                  <div>
                    <strong>Erstellt:</strong>{' '}
                    {new Date(pdf.created_at).toLocaleString('de-DE')}
                  </div>
                  <div>
                    <strong>Datensatz:</strong> #{pdf.data_row + 1}
                  </div>
                </div>
                <div className="card-actions">
                  <button
                    onClick={() => downloadGeneratedPDF(pdf.id, pdf.file_name)}
                    className="btn-small btn-primary"
                    style={{ flex: 1, textAlign: 'center' }}
                  >
                    📥 Download
                  </button>
                  <button
                    onClick={() => handleDeletePDF(pdf.id, pdf.file_name)}
                    className="btn-small btn-danger"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PDFGeneratePage;
